// Csatolmányok feltöltése és törlése (W13).
//
// A tárolási útvonalak SZÁNDÉKOSAN ugyanazok, mint a Flutter oldalon
// (lib/pages/tasks/repo/tasks_attachments_repo.dart):
//
//   task_images_by_user/{taskId}/{fájlnév}   — teljesítéskor feltöltött kép
//   task_files_by_user/{taskId}/{fájlnév}    — teljesítéskor feltöltött fájl
//
// A két felület UGYANAZT az adatot írja, ezért a séma sem térhet el: a
// feladatdokumentumban `{ url, type }` alakú bejegyzés keletkezik az
// `attachmentsSubmittedByUser` tömbben.

import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
  uploadBytesResumable,
} from 'firebase/storage';
import { doc, setDoc, type Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';
import { t } from './i18n';
import { updateTaskRaw, type WriteContext } from './writes';

/// A feltöltési réteg kapcsolatai.
///
/// A modul SZÁNDÉKOSAN nem importálja a `./firebase`-t: az a Vite környezeti
/// változóit várja, tehát Node alatt (ellenőrző szkriptben) el sem indulna.
/// Ugyanaz a minta, mint a `WriteContext` és a `MasterDataContext` esetében —
/// és ugyanazért: így a réteg emulátor ellen is futtatható, valódi adat
/// érintése nélkül.
export type UploadContext = WriteContext & { storage: FirebaseStorage };

export type UploadKind = 'photo' | 'file';

export function uploadKindOf(file: File): UploadKind {
  return file.type.startsWith('image/') ? 'photo' : 'file';
}

/// A Storage-útvonalon a fájlnév ütközhet. Időbélyeggel egyedivé tesszük —
/// a Flutter oldal ugyanezt a mappát használja, ezért felülírás helyett
/// inkább új nevet adunk.
function storagePathFor(taskId: string, file: File): string {
  const kind = uploadKindOf(file);
  const folder = kind === 'photo' ? 'task_images_by_user' : 'task_files_by_user';
  const safe = file.name.replace(/[^\w.\-]+/g, '_');
  return `${folder}/${taskId}/${Date.now()}_${safe}`;
}

/// Egy fájl feltöltése haladásjelzéssel.
///
/// A haladás a terv (W13) folyamatsávját táplálja: feltöltés közben a fájl
/// már ott áll a listában, százalékkal — nem a semmiből jelenik meg a végén.
export function uploadAttachment(
  storage: FirebaseStorage,
  taskId: string,
  file: File,
  onProgress?: (ratio: number) => void,
): { promise: Promise<{ url: string; kind: UploadKind }>; cancel: () => void } {
  const task = uploadBytesResumable(ref(storage, storagePathFor(taskId, file)), file, {
    contentType: file.type || 'application/octet-stream',
  });

  const promise = new Promise<{ url: string; kind: UploadKind }>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        if (snapshot.totalBytes > 0) {
          onProgress?.(snapshot.bytesTransferred / snapshot.totalBytes);
        }
      },
      reject,
      () => {
        getDownloadURL(task.snapshot.ref)
          .then((url) => resolve({ url, kind: uploadKindOf(file) }))
          .catch(reject);
      },
    );
  });

  return { promise, cancel: () => task.cancel() };
}

/// A feltöltött csatolmányok bejegyzése a feladatra.
export async function attachUploadedFiles(
  ctx: WriteContext,
  taskId: string,
  items: { url: string; kind: UploadKind }[],
): Promise<void> {
  if (items.length === 0) return;

  await updateTaskRaw(ctx, taskId, (task) => {
    const existing = Array.isArray(task['attachmentsSubmittedByUser'])
      ? [...(task['attachmentsSubmittedByUser'] as unknown[])]
      : [];
    for (const item of items) {
      existing.push({ url: item.url, type: item.kind });
    }
    task['attachmentsSubmittedByUser'] = existing;
  });
}

/// Csatolmány eltávolítása: előbb a feladatról, utána a tárolóból.
///
/// Ebben a sorrendben: ha a Storage-törlés elhasal (pl. már nincs ott a fájl),
/// a feladaton akkor se maradjon ott egy megnyithatatlan hivatkozás.
export async function removeAttachment(
  ctx: UploadContext,
  taskId: string,
  url: string,
): Promise<void> {
  await updateTaskRaw(ctx, taskId, (task) => {
    for (const key of ['attachmentsAssignedToTask', 'attachmentsSubmittedByUser']) {
      const list = Array.isArray(task[key]) ? (task[key] as Record<string, unknown>[]) : [];
      task[key] = list.filter((item) => item['url'] !== url);
    }
  });

  try {
    await deleteObject(ref(ctx.storage, url));
  } catch {
    // A tarolo felol mar hianyzo fajl nem hiba: a feladatrol mar levettuk.
  }
}

/// Az aláírás mentése (W14).
///
/// A kép data URL-ként érkezik a vászonról; a tárolási útvonal
/// `task_signatures/{taskId}/…`, ugyanúgy, mint a Flutter oldalon. A
/// feladaton a `signature` mező egyetlen `{ url, type }` bejegyzés: mindig
/// csak egy aláírás van érvényben, a mentés felülírja a korábbit.
export async function saveSignature(
  ctx: UploadContext,
  taskId: string,
  dataUrl: string,
): Promise<void> {
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], `alairas_${Date.now()}.png`, { type: 'image/png' });

  const target = ref(ctx.storage, `task_signatures/${taskId}/${file.name}`);
  await uploadBytes(target, file, { contentType: 'image/png' });
  const url = await getDownloadURL(target);

  await updateTaskRaw(ctx, taskId, (task) => {
    task['signature'] = { url, type: 'photo' };
  });
}

/// Profilkép feltöltése (W24 / W25).
///
/// Az útvonal a Flutter oldallal KÖZÖS: `profile_pictures/{userId}.jpg`
/// (lásd `UsersRepo.editUser`). Ez SZÁNDÉKOSAN nem kap időbélyeget, ahogy a
/// csatolmányok: egy felhasználónak egy profilképe van, az új felülírja a
/// régit. Időbélyeggel minden csere után ott maradna egy árva fájl.
///
/// A `contentType` kötelezően `image/jpeg`, mert a mobil `.jpg` néven írja,
/// és a Storage a kiterjesztésből nem következtet típusra — enélkül a kép
/// `application/octet-stream`-ként jönne vissza, és a böngésző letöltésként
/// kezelné.
export async function uploadProfilePhoto(
  // A kapcsolatok KÍVÜLRŐL jönnek, nem a modul globális példányaiból —
  // ugyanaz a minta, mint a `MasterDataContext` és a `WriteContext`
  // esetében, és ugyanazért: így a függvény emulátor ellen is futtatható,
  // valódi adat érintése nélkül. A globális `storage`-dzsal az ellenőrzés
  // csak a szerződést tudná lemérni, magát a kódot nem.
  deps: { db: Firestore; storage: FirebaseStorage },
  userId: string,
  file: File,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error(t('user.profilePhotoNotImage'));
  }

  const target = ref(deps.storage, `profile_pictures/${userId}.jpg`);
  await uploadBytes(target, file, { contentType: 'image/jpeg' });
  const url = await getDownloadURL(target);

  // A hivatkozás a felhasználó dokumentumára kerül — `merge: true`, mert a
  // dokumentumon minden más adata is ott van.
  await setDoc(doc(deps.db, 'users', userId), { photoUrl: url }, { merge: true });
  return url;
}
